/**
 * usgsConnector.ts
 *
 * Fetches USGS earthquake GeoJSON feed and converts to Float32Array
 * with stride 5: [lon, lat, altMeters, value, rotationRad]
 *
 * rotationRad is always 0.0 for earthquakes (no heading).
 * stride-5 matches scOverlayPass instance buffer layout.
 *
 * CORS: YES — USGS public feed supports direct browser fetch.
 * Refresh interval: 60 seconds (USGS server cache time).
 */

export const USGS_FEED_URL =
  'https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/all_day.geojson';

/** Number of floats per marker: lon, lat, alt, value, rotationRad */
export const USGS_STRIDE = 5;

interface USGSFeature {
  geometry: {
    coordinates: [number, number, number]; // [lon, lat, depthKm]
  };
  properties: {
    mag: number | null;
    time: number;
    place: string;
  };
}

interface USGSGeoJSON {
  features: USGSFeature[];
}

/**
 * Fetch the USGS all-day earthquake feed and convert to a Float32Array.
 *
 * Layout per entry (stride=5):
 *   [0] lon           — WGS84 longitude in degrees
 *   [1] lat           — WGS84 latitude in degrees
 *   [2] altMeters     — depthKm * -1000 (negative = below surface)
 *   [3] value         — magnitude / 9.0, clamped to [0, 1]; null mag → 0
 *   [4] rotationRad   — always 0.0 (earthquakes have no heading)
 *
 * @returns Float32Array of length features.length * 5, or null on error.
 */
export async function fetchUSGSEarthquakes(): Promise<Float32Array | null> {
  // AbortSignal.timeout may not be available in all environments (e.g. older jsdom)
  const signal = typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(10_000)
    : undefined;

  let response: Response;
  try {
    response = await fetch(USGS_FEED_URL, { signal });
  } catch {
    return null;
  }

  if (!response.ok) return null;

  const json: USGSGeoJSON = await response.json();
  const features = json.features;
  const buf = new Float32Array(features.length * USGS_STRIDE);

  let i = 0;
  for (const f of features) {
    const [lon, lat, depthKm] = f.geometry.coordinates;
    const mag = f.properties.mag ?? 0;
    const altMeters = (depthKm ?? 0) * -1000;
    const value = Math.max(0, Math.min(1, mag / 9.0));

    buf[i++] = lon;
    buf[i++] = lat;
    buf[i++] = altMeters;
    buf[i++] = value;
    buf[i++] = 0.0;  // rotationRad — earthquakes have no heading
  }

  return buf;
}
